import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check, Package, ShieldCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSelections } from './SelectionsContext';
import { useTheme } from '../styles/ThemeContext';
interface PackageGalleryProps {
  onClose: () => void;
  docked?: boolean;
}
interface PackageItem {
  category: string;
  name: string;
  price: number;
  thumbnail: string;
}
interface PackageTier {
  id: string;
  name: string; // "Standard", "Level 1", etc.
  label: string; // "Standard", "Package 1", "Package 2", etc.
  price: number;
  originalPrice: number;
  description: string;
  items: PackageItem[];
  color: string;
  badgeColor: string;
}
const PACKAGES: PackageTier[] = [{
  id: 'pkg-standard',
  name: 'Standard',
  label: 'Standard',
  price: 0,
  originalPrice: 0,
  description: 'Essential appliances for everyday living',
  color: 'emerald',
  badgeColor: 'bg-emerald-500',
  items: [{
    category: 'Refrigerator',
    name: 'Standard 30"',
    price: 0,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/56xcQygN48ytC9vCWfbKsD/2a3e8f16-fd9a-4893-b4aa-68846e8eee8d.png'
  }, {
    category: 'Range Oven',
    name: 'Standard',
    price: 0,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/ipGLrgJ5dnUHzeX467UrRX/99451023-beef-4830-b2e4-6b5c5bbc05fa.png'
  }, {
    category: 'Dishwasher',
    name: 'Standard',
    price: 0,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/8WnWtNRoYDXghwDDYKxMY1/3a7bf4b8-f2ce-43ab-812d-d2135155715f.png'
  }, {
    category: 'Microwave',
    name: 'Standard',
    price: 0,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/7J9vR4HKQ2tpt65jg7G16W/74bb9c06-756a-4000-8501-7b776559d1c5.png'
  }, {
    category: 'Countertop',
    name: 'Laminate',
    price: 0,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/mVj6c7zkUqrRquwCmxWcL6/15d95c32-8149-4132-9785-f1bfe49bb891.png'
  }]
}, {
  id: 'pkg-level-1',
  name: 'Level 1',
  label: 'Package 1',
  price: 5900,
  originalPrice: 6700,
  description: 'Upgraded stainless steel finish and better efficiency',
  color: 'blue',
  badgeColor: 'bg-blue-500',
  items: [{
    category: 'Refrigerator',
    name: 'Side-by-Side',
    price: 1200,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/4rsTQoSw8cboz4u88BHB6e/31666bba-be57-4389-96b2-845051abc30a.png'
  }, {
    category: 'Range Oven',
    name: 'Stainless Steel',
    price: 1500,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/7qRjsZBrCQMtsgwG6dR6aa/a12724ab-875b-49f0-82d7-cbbbc5f8d329.png'
  }, {
    category: 'Dishwasher',
    name: 'Ultra Quiet',
    price: 450,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/9HgKuy26y4nFyrfLCdGjh9/43af1ca4-cf13-471b-bebe-8bd2e03fcbfb.png'
  }, {
    category: 'Microwave',
    name: 'Over-the-Range',
    price: 350,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/raVYgQuqvg9FTcFXiVoWdF/d1779e5a-0f26-4813-b30b-c76d034a46e4.png'
  }, {
    category: 'Countertop',
    name: 'Butcher Block',
    price: 3200,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/f1hzbo3o3QLATPgnhLZGui/c9b16336-b6de-4ae1-8665-58f652453ea2.png'
  }]
}, {
  id: 'pkg-level-2',
  name: 'Level 2',
  label: 'Package 2',
  price: 9200,
  originalPrice: 10250,
  description: 'Professional style with premium features',
  color: 'amber',
  badgeColor: 'bg-amber-500',
  items: [{
    category: 'Refrigerator',
    name: 'French Door 36"',
    price: 1800,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/sj8L76yNDFjCGFZWdgGY4N/9d9925e9-c3ea-4a0d-80fb-ffcfee4e866e.png'
  }, {
    category: 'Range Oven',
    name: 'Professional',
    price: 2500,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/rBmiA4HJkMsYLa3DUGGUeP/a38be756-541f-4f67-b58f-3e7cff68d49c.png'
  }, {
    category: 'Dishwasher',
    name: 'Third Rack',
    price: 650,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/trdsaseMYftX5ReXeRVWmo/728deced-e8d7-48dc-a7f2-8c641afae38b.png'
  }, {
    category: 'Microwave',
    name: 'Built-In',
    price: 800,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/4o8ktMaBmPjywuSkMVi4Um/e2e961b3-6f11-40d0-b1be-f04ad41d6c36.png'
  }, {
    category: 'Countertop',
    name: 'Granite',
    price: 4500,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/kpH5AVoc585CmkhtFwT2gu/bb2e92e2-95c2-4917-a8f9-e317d073068e.png'
  }]
}, {
  id: 'pkg-level-3',
  name: 'Level 3',
  label: 'Package 3',
  price: 12500,
  originalPrice: 13450,
  description: 'Chef-inspired appliances for culinary excellence',
  color: 'purple',
  badgeColor: 'bg-purple-500',
  items: [{
    category: 'Refrigerator',
    name: 'Counter Depth',
    price: 2400,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/8uMrohCuZZGCZe6mp1Ebh5/bdab08d5-af5f-4768-afba-d5b3c4e4d4f4.png'
  }, {
    category: 'Range Oven',
    name: 'Chef Series',
    price: 3500,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/i3anGUuRyR4DYNoRawPTEF/ba5cadb3-2c6f-4c61-b770-e245581bd9ca.png'
  }, {
    category: 'Dishwasher',
    name: 'Panel Ready',
    price: 850,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/gXch4QkMwxNQ9Gf4G5EAdJ/b394dd48-02c4-4c34-a060-d76f1604fa7c.png'
  }, {
    category: 'Microwave',
    name: 'Drawer Style',
    price: 1200,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/9g5j9FPCgRFZUjU4MVxTD6/dcf7809d-faed-42b2-844a-3dab62702865.png'
  }, {
    category: 'Countertop',
    name: 'Quartz',
    price: 5500,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/hA4ppMCeeEnfUr8DdemFhK/80b42e57-750f-436c-8070-ab27d0716845.png'
  }]
}, {
  id: 'pkg-level-4',
  name: 'Level 4',
  label: 'Package 4',
  price: 17500,
  originalPrice: 18900,
  description: 'Top-of-the-line luxury and smart technology',
  color: 'rose',
  badgeColor: 'bg-rose-500',
  items: [{
    category: 'Refrigerator',
    name: 'Smart Hub',
    price: 3500,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/rWjZv6Mm4vUpRox9hUrK2K/92c595c0-b3df-4f96-84f9-414aad4eea5b.png'
  }, {
    category: 'Range Oven',
    name: 'Commercial Grade',
    price: 4500,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/j59ZjLMVTNqtgapFimQc1E/08ea5d1f-e4b6-4d72-8184-56c394b9cb43.png'
  }, {
    category: 'Dishwasher',
    name: 'Smart Connect',
    price: 1200,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/iQkCQnTBXVuPRJZw5CWi23/a3b5acff-a1c0-4442-84b1-286862e88499.png'
  }, {
    category: 'Microwave',
    name: 'Drawer Style',
    price: 1200,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/9g5j9FPCgRFZUjU4MVxTD6/dcf7809d-faed-42b2-844a-3dab62702865.png'
  }, {
    category: 'Countertop',
    name: 'Marble',
    price: 8500,
    thumbnail: 'https://cdn.magicpatterns.com/uploads/xefSXa2PcHLjXAgiprF9C2/94335722-d546-4e85-83cf-af86687637c9.png'
  }]
}];
export function PackageGallery({
  onClose,
  docked = false
}: PackageGalleryProps) {
  const {
    activePackage,
    setActivePackage,
    clearActivePackage
  } = useSelections();
  const {
    isDark
  } = useTheme();
  const [viewedPackageId, setViewedPackageId] = useState<string>(activePackage?.id || 'pkg-level-1');
  // Update viewed package if active package changes externally
  useEffect(() => {
    if (activePackage?.id) {
      setViewedPackageId(activePackage.id);
    }
  }, [activePackage]);
  const viewedPackage = PACKAGES.find(p => p.id === viewedPackageId) || PACKAGES[1];
  const isActive = activePackage?.id === viewedPackageId;
  const savings = viewedPackage.originalPrice - viewedPackage.price;
  const handleSelectPackage = () => {
    setActivePackage({
      id: viewedPackage.id,
      tier: viewedPackage.name,
      category: 'Kitchen',
      price: viewedPackage.price
    });
  };
  const handleRemovePackage = () => {
    clearActivePackage();
  };
  return <motion.div initial={{
    y: '100%'
  }} animate={{
    y: 0
  }} exit={{
    y: '100%'
  }} transition={{
    type: 'spring',
    damping: 25,
    stiffness: 200
  }} className={docked ? 'w-full pointer-events-none' : 'absolute bottom-[60px] left-[260px] right-0 z-30 flex justify-center pointer-events-none px-6'} data-id="element-1467">
      <div className={`backdrop-blur-xl border shadow-2xl pointer-events-auto overflow-hidden transition-all duration-300 flex flex-col
          ${docked ? 'w-full rounded-t-2xl border-b-0' : 'w-full max-w-5xl rounded-2xl'} 
          ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-white/40'}
          max-h-[500px]
        `} data-id="element-1468">
        {/* Header */}
        <div className={`flex flex-col border-b ${isDark ? 'border-white/5 bg-white/5' : 'border-gray-200/50 bg-gray-50/50'}`} data-id="element-1469">
          {/* Top Bar */}
          <div className="flex items-center justify-between px-4 py-2" data-id="element-1470">
            <div className="flex items-center gap-3" data-id="element-1471">
              <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-1472">
                Select Package
              </span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-teal-500/20 text-teal-600'}`} data-id="element-1473">
                Kitchen Packages
              </span>
            </div>
            <button onClick={onClose} className={`p-1.5 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-white/50 hover:text-white' : 'hover:bg-gray-200 text-gray-400 hover:text-gray-600'}`} data-id="element-1474">
              <ChevronDown className="w-4 h-4" data-id="element-1475" />
            </button>
          </div>

          {/* Tier Pills (Tabs) */}
          <div className="px-4 pb-2 flex items-center gap-2 overflow-x-auto custom-scrollbar no-scrollbar" data-id="element-1476">
            {PACKAGES.map(pkg => {
            const isSelected = viewedPackageId === pkg.id;
            const isPackageActive = activePackage?.id === pkg.id;
            return <button key={pkg.id} onClick={() => setViewedPackageId(pkg.id)} className={`
                    flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap border
                    ${isSelected ? isDark ? 'bg-cyan-500 text-white border-cyan-400 shadow-lg shadow-cyan-500/20' : 'bg-teal-600 text-white border-teal-500 shadow-lg shadow-teal-600/20' : isDark ? 'bg-white/5 text-white/60 border-transparent hover:bg-white/10 hover:text-white' : 'bg-gray-100 text-gray-600 border-transparent hover:bg-gray-200 hover:text-gray-900'}
                  `} data-id="element-1477">
                  {pkg.label}
                  {isPackageActive && <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" data-id="element-1478" />}
                </button>;
          })}
          </div>
        </div>

        {/* Package Summary Bar */}
        <div className={`px-6 py-4 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`} data-id="element-1479">
          <div data-id="element-1480">
            <div className="flex items-center gap-2 mb-1" data-id="element-1481">
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-white ${viewedPackage.badgeColor}`} data-id="element-1482">
                {viewedPackage.name}
              </span>
              <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1483">
                {viewedPackage.description}
              </h3>
            </div>
          </div>

          <div className="flex items-center gap-4" data-id="element-1484">
            <div className="text-right" data-id="element-1485">
              <div className="flex items-baseline justify-end gap-2" data-id="element-1486">
                <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1487">
                  {viewedPackage.price === 0 ? 'Included' : `$${viewedPackage.price.toLocaleString()}`}
                </span>
                {savings > 0 && <span className={`text-sm line-through ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-1488">
                    ${viewedPackage.originalPrice.toLocaleString()}
                  </span>}
              </div>
              {savings > 0 && <div className={`text-xs font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} data-id="element-1489">
                  Save ${savings.toLocaleString()}
                </div>}
            </div>

            {isActive ? <button onClick={handleRemovePackage} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold transition-all ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-200 text-gray-900 hover:bg-gray-300'}`} data-id="element-1490">
                Remove Package
              </button> : <button onClick={handleSelectPackage} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold shadow-lg transition-all transform hover:scale-105 active:scale-95 ${isDark ? 'bg-cyan-500 text-white hover:bg-cyan-400 shadow-cyan-500/20' : 'bg-teal-600 text-white hover:bg-teal-500 shadow-teal-600/20'}`} data-id="element-1491">
                Select Package
              </button>}
          </div>
        </div>

        {/* Appliance Thumbnails Row */}
        <div className="p-6 overflow-x-auto custom-scrollbar" data-id="element-1492">
          <div className="flex gap-4 min-w-min pb-2" data-id="element-1493">
            {viewedPackage.items.map((item, index) => <div key={index} className="flex flex-col items-center gap-2 w-28 flex-shrink-0" data-id="element-1494">
                {/* Thumbnail */}
                <div className={`
                  relative w-28 h-28 rounded-xl overflow-hidden border-2 transition-all
                  ${isDark ? 'border-white/10 bg-slate-800' : 'border-gray-200 bg-gray-100'}
                `} data-id="element-1495">
                  <img src={item.thumbnail} alt={item.name} className="w-full h-full object-cover" data-id="element-1496" />
                  {/* Category Badge */}
                  <div className={`
                    absolute bottom-0 inset-x-0 py-1 text-[9px] font-bold text-center uppercase tracking-wider backdrop-blur-md
                    ${isDark ? 'bg-black/60 text-white/90' : 'bg-white/80 text-gray-800'}
                  `} data-id="element-1497">
                    {item.category}
                  </div>
                </div>

                {/* Details */}
                <div className="text-center w-full" data-id="element-1498">
                  <div className={`text-[11px] font-medium truncate w-full ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1499">
                    {item.name}
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </motion.div>;
}